import { useEffect, useState } from "react";
import FlexRow from "../../../../components/base/FlexRow";
import { toNormalDate, toShortDate, toShortDateWithoutTime, useHub, usePrices } from "../../../../core/Global";
import Modal from "../../../../components/base/Modal";
import PaymentMethodPicker, { NEW_CARD } from "./PaymentMethodPicker";
import UsersAddonModel, { AddonTypes } from "../models/UsersAddonModel";
import Div from "../../../../components/base/Div";
import Indicator from "../../../../components/base/Indicator";
import { toastError, toastSuccess } from "../../../../components/base/Toast";
import { Drawer } from "../../../../components/drawers/Drawer";
import { DrawerForm } from "../../../../components/drawers/DrawerForm";
import Icon from "../../../../components/base/Icon";



export default function AddonsManage() {

    const moneyFormatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
    });

    const hub = useHub()
    const prices = usePrices()
    const [loading, loadingSet] = useState<boolean>(true)
    const [addons, addonsSet] = useState<UsersAddonModel[]>([])

    const [whatsAppAddon, whatsAppAddonSet] = useState<UsersAddonModel>()
    const [telegramAddon, telegramAddonSet] = useState<UsersAddonModel>()

    // The selected payment method in whichever activation drawer is open (a saved card id or NEW_CARD).
    const [selectedMethodId, selectedMethodIdSet] = useState<string>()

    const fetch = () => {
        hub.Get("/accounts/profile/addons", res => {
            if (res.success) addonsSet(res.data)
            loadingSet(false)
        })
    }

    useEffect(fetch, [])

    // Toast + refresh the addon section after a new-card Checkout returns (kind=addon on the URL).
    useEffect(() => {
        const params = new URLSearchParams(window.location.search)
        if (params.get("kind") !== "addon") return
        const payment = params.get("payment")

        if (payment === "success") { toastSuccess("Your addon is now active!"); fetch(); }
        else if (payment === "failed") toastError(params.get("message") || "The addon payment failed.")

        params.delete("payment"); params.delete("message"); params.delete("kind")
        const clean = window.location.pathname + (params.toString() ? "?" + params.toString() : "")
        window.history.replaceState({}, "", clean)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])


    useEffect(() => {
        if (addons.length > 0) {
            var wa = addons.find(f => f.addonType == AddonTypes.WhatsApp)
            whatsAppAddonSet(wa)

            var tg = addons.find(f => f.addonType == AddonTypes.Telegram)
            telegramAddonSet(tg)
        }
    }, [addons])

    const [activateSeats, activateSeatsSet] = useState<boolean>(false)
    const [newSeats, newSeatsSet] = useState<number>(1)
    const [seatsVerifyResult, seatsVerifyResultSet] = useState<any>()

    const verifySeats = (seats: any) => {

        hub.Post("/accounts/profile/addon/seats/verify", {
            extraSeats: seats
        }, res => {
            if (res.success) seatsVerifyResultSet(res.data);
        });
    }

    const [activateWhatsApp, activateWhatsAppSet] = useState<boolean>(false)

    const [activateTelegram, activateTelegramSet] = useState<boolean>(false)
    const [cancelTelegram, cancelTelegramSet] = useState<boolean>(false)


    // WhatsApp/Telegram carry a one-time 30-day trial: available only if no addon of that type has ever
    // existed (records persist through expiry/cancel), matching the backend's trial guard.
    const whatsAppTrial = !whatsAppAddon
    const telegramTrial = !telegramAddon

    // Pays for an addon with the chosen method: a saved card charges inline; New Card redirects to Checkout.
    const payAddon = (type: AddonTypes, count: number | undefined, callback: any) => {
        if (selectedMethodId === NEW_CARD) {
            hub.Post("/accounts/profile/addon/pay/newcard", { type, count }, res => {
                if (res.success && res.data?.url) {
                    document.location.href = res.data.url
                    return
                }
                toastError(res.message ?? "Couldn't start the payment.")
                callback!(res)
            })
            return
        }

        hub.Post("/billings/payments/addons", { type, count, paymentMethodId: selectedMethodId }, res => {
            if (res.success) addonsSet(res.data)
            else toastError(res.message)
            callback!(res)
        })
    }

    // Starts a free trial (no charge, no payment method).
    const startTrial = (type: AddonTypes, callback: any) => {
        hub.Post("/billings/payments/addons", { type }, res => {
            if (res.success) addonsSet(res.data)
            else toastError(res.message)
            callback!(res)
        })
    }


    return (
        <>
            <div className="card mb-5">
                <div className="card-header border-0">
                    <div className="card-title m-0">
                        <h3 className="fw-bolder m-0">Addons</h3>
                    </div>
                </div>
                <div className="card-body border-top p-9">

                    <Div visible={loading} className="py-10">
                        <Indicator />
                    </Div>

                    <Div visible={!loading}>

                        <FlexRow className="py-5">
                            <img src="/assets/addons/seats.png" className="w-65px me-4" />
                            <div>
                                <div className="fw-bold fs-4 mb-1">
                                    Extra Seats Package
                                </div>
                                <div className="fs-7">
                                    {moneyFormatter.format(prices?.extra5SeatsPackage ?? 0)}/Month (5 Extra Seats)
                                </div>
                            </div>

                            <div className="ms-auto">
                                <button className="btn btn-primary"
                                    onClick={e => activateSeatsSet(true)}>
                                    Add Packages
                                </button>
                            </div>
                        </FlexRow>

                        {
                            addons.filter(f => f.addonType == AddonTypes.ExtraSeat).map(ad =>
                                <>
                                    <hr style={{
                                        marginLeft: 75
                                    }} />
                                    <FlexRow key={`addon-extraseats-${ad.id}`}>
                                        <div className="w-75px">&nbsp;</div>

                                        {
                                            ad.expired ?
                                                <span className="badge badge-danger fs-6 me-5">
                                                    Expired
                                                </span>
                                                :
                                                <span className="badge badge-success fs-6 me-5">
                                                    Active
                                                </span>
                                        }

                                        <Icon icon="headset" className="me-2" size={16} />
                                        <span className="me-5">
                                            {ad.count} Extra Seats ({(ad.count ?? 0) * 5} Users)
                                        </span>
                                        {
                                            ad.expired ?
                                                <></>
                                                : ad.canceled ?
                                                    <span className="text-danger fs-7">
                                                        Will be canceled on {toShortDateWithoutTime(ad.expiresAt)}
                                                    </span>
                                                    : ad.active ?
                                                        <span className="fs-7">
                                                            Next payment: {moneyFormatter.format((prices?.extra5SeatsPackage ?? 0) * (ad.count ?? 1))} on {toShortDateWithoutTime(ad.expiresAt)}
                                                        </span>
                                                        : <></>
                                        }


                                        <div className="ms-auto">
                                            {
                                                ad.canceled ?
                                                    <button className="btn btn-sm btn-outline btn-outline-warning btn-active-light-warning"
                                                        onClick={e => {
                                                            hub.Post("/accounts/profile/addons/undocancel", {
                                                                id: ad.id
                                                            }, res => {
                                                                if (res.success) {
                                                                    addonsSet(res.data)
                                                                }
                                                            })
                                                        }}>
                                                        Undo Cancellation
                                                    </button>
                                                    : ad.active ?
                                                        <button className="btn btn-sm btn-outline btn-outline-danger btn-active-light-danger"
                                                            onClick={e => {

                                                                hub.Post("/accounts/profile/addons/cancel", {
                                                                    id: ad.id
                                                                }, res => {
                                                                    if (res.success) {
                                                                        addonsSet(res.data)
                                                                    }
                                                                })

                                                            }}>
                                                            Cancel
                                                        </button>
                                                        : <></>
                                            }
                                        </div>
                                    </FlexRow>
                                </>
                            )
                        }

                        <hr />

                        <FlexRow className="py-5">
                            <img src="/assets/addons/whatsapp.png" className="w-65px me-4" />
                            <div>
                                <div className="fw-bold fs-4 mb-1">
                                    WhatsApp Integration
                                </div>
                                {
                                    whatsAppAddon?.expired ?
                                        <FlexRow className="mt-1">
                                            <span className="badge badge-danger fs-6 me-5">
                                                Expired
                                            </span>
                                            <span className="fs-7">
                                                Activate again with {moneyFormatter.format(prices?.whatsAppIntegration ?? 0)}/Month
                                            </span>
                                        </FlexRow>
                                        : whatsAppAddon?.canceled ?
                                            <FlexRow className="mt-1">
                                                <span className="badge badge-success fs-6 me-5">
                                                    Active
                                                </span>
                                                <span className="text-danger fs-7">
                                                    Will be canceled on {toShortDateWithoutTime(whatsAppAddon.expiresAt)}
                                                </span>
                                            </FlexRow>
                                            : whatsAppAddon?.active ?
                                                <FlexRow className="mt-1">
                                                    <span className="badge badge-success fs-6 me-5">
                                                        Active
                                                    </span>
                                                    <span className="fs-7">
                                                        Next payment: {moneyFormatter.format(prices?.whatsAppIntegration ?? 0)} on {toShortDateWithoutTime(whatsAppAddon.expiresAt)}
                                                    </span>
                                                </FlexRow>
                                                :
                                                <div className="fs-7">
                                                    {moneyFormatter.format(prices?.whatsAppIntegration ?? 0)}/Month (30 Days Trial)
                                                </div>
                                }

                            </div>

                            <div className="ms-auto">
                                {
                                    whatsAppAddon?.canceled ?
                                        <button className="btn btn-sm btn-outline btn-outline-warning btn-active-light-warning"
                                            onClick={e => {
                                                hub.Post("/accounts/profile/addons/undocancel", {
                                                    id: whatsAppAddon.id
                                                }, res => {
                                                    if (res.success) {
                                                        addonsSet(res.data)
                                                    }
                                                })
                                            }}>
                                            Undo Cancellation
                                        </button>
                                        : whatsAppAddon?.active ?
                                            <button className="btn btn-sm btn-outline btn-outline-danger btn-active-light-danger"
                                                onClick={e => {
                                                    hub.Post("/accounts/profile/addons/cancel", {
                                                        id: whatsAppAddon.id
                                                    }, res => {
                                                        if (res.success) {
                                                            addonsSet(res.data)
                                                        }
                                                    })
                                                }}>
                                                Cancel
                                            </button>
                                            : whatsAppAddon?.expired ?
                                                <button className="btn btn-primary"
                                                    onClick={e => activateWhatsAppSet(true)}>
                                                    Activate
                                                </button>
                                                :
                                                <button className="btn btn-primary"
                                                    onClick={e => activateWhatsAppSet(true)}>
                                                    Start Free Trial
                                                </button>
                                }
                            </div>
                        </FlexRow>

                        <hr />

                        <FlexRow className="py-5">
                            <img src="/assets/addons/telegram.png" className="w-65px me-4" />
                            <div>
                                <div className="fw-bold fs-4 mb-1">
                                    Telegram Integration
                                </div>
                                {
                                    telegramAddon?.expired ?
                                        <FlexRow className="mt-1">
                                            <span className="badge badge-danger fs-6 me-5">
                                                Expired
                                            </span>
                                            <span className="fs-7">
                                                Activate again with {moneyFormatter.format(prices?.telegramIntegration ?? 0)}/Month
                                            </span>
                                        </FlexRow>
                                        : telegramAddon?.canceled ?
                                            <FlexRow className="mt-1">
                                                <span className="badge badge-success fs-6 me-5">
                                                    Active
                                                </span>
                                                <span className="text-danger fs-7">
                                                    Will be canceled on {toShortDateWithoutTime(telegramAddon.expiresAt)}
                                                </span>
                                            </FlexRow>
                                            : telegramAddon?.active ?
                                                <FlexRow className="mt-1">
                                                    <span className="badge badge-success fs-6 me-5">
                                                        Active
                                                    </span>
                                                    <span className="fs-7">
                                                        Next payment: {moneyFormatter.format(prices?.telegramIntegration ?? 0)} on {toShortDateWithoutTime(telegramAddon.expiresAt)}
                                                    </span>
                                                </FlexRow>
                                                :
                                                <div className="fs-7">
                                                    {moneyFormatter.format(prices?.telegramIntegration ?? 0)}/Month (30 Days Trial)
                                                </div>
                                }
                            </div>

                            <div className="ms-auto">
                                {
                                    telegramAddon?.canceled ?
                                        <button className="btn btn-sm btn-outline btn-outline-warning btn-active-light-warning"
                                            onClick={e => {
                                                hub.Post("/accounts/profile/addons/undocancel", {
                                                    id: telegramAddon.id
                                                }, res => {
                                                    if (res.success) {
                                                        addonsSet(res.data)
                                                    }
                                                })
                                            }}>
                                            Undo Cancellation
                                        </button>
                                        : telegramAddon?.active ?
                                            <button className="btn btn-sm btn-outline btn-outline-danger btn-active-light-danger"
                                                onClick={e => {
                                                    hub.Post("/accounts/profile/addons/cancel", {
                                                        id: telegramAddon.id
                                                    }, res => {
                                                        if (res.success) {
                                                            addonsSet(res.data)
                                                        }
                                                    })
                                                }}>
                                                Cancel
                                            </button>
                                            : telegramAddon?.expired ?
                                                <button className="btn btn-primary"
                                                    onClick={e => activateTelegramSet(true)}>
                                                    Activate
                                                </button>
                                                :
                                                <button className="btn btn-primary"
                                                    onClick={e => activateTelegramSet(true)}>
                                                    Start Free Trial
                                                </button>

                                }
                            </div>
                        </FlexRow>
                    </Div>

                </div>
            </div>

            {
                activateSeats &&
                <DrawerForm id="modal-activate-seats"
                    title="Activate Addon"
                    buttonText="Confirm & Pay"
                    onClose={() => activateSeatsSet(false)}
                    onOpen={() => {
                        newSeatsSet(1)
                    }}
                    onSubmit={(body, callback) => {
                        payAddon(AddonTypes.ExtraSeat, newSeats, callback)
                    }}>
                    <div>

                        <div className="mb-5">

                            <FlexRow className="mb-2 ps-1">
                                <span>
                                    Select Seats
                                </span>
                            </FlexRow>

                            <select className="form-select form-select-sm w-100"
                                value={newSeats}
                                onChange={e => {
                                    var st = Number.parseInt(e.target.value);
                                    newSeatsSet(st);
                                }}>
                                {
                                    [...Array(10)].map((item, i) => {
                                        const val = i + 1;
                                        return (
                                            <option key={val} value={val}>
                                                {val + " Extra Package (" + (val * 5) + " Users)"}
                                            </option>
                                        )
                                    })
                                }
                            </select>

                        </div>


                        <FlexRow className="mb-8">
                            <span className="me-2">Total:</span>
                            <span className="fs-2">{moneyFormatter.format((prices?.extra5SeatsPackage ?? 0) * (newSeats))}</span>
                        </FlexRow>

                        <div className="fw-bold mb-3">Payment Method</div>
                        <PaymentMethodPicker selectedMethodId={selectedMethodId} onChange={selectedMethodIdSet} />

                    </div>
                </DrawerForm>
            }

            {
                activateWhatsApp &&
                <DrawerForm id="modal-activate-whatsapp-trial"
                    title="Activate Addon"
                    buttonText={whatsAppTrial ? "Start Free Trial" : `Activate & Pay ${moneyFormatter.format(prices?.whatsAppIntegration ?? 0)}`}
                    onClose={() => activateWhatsAppSet(false)}
                    onSubmit={(body, callback) => {
                        if (whatsAppTrial) startTrial(AddonTypes.WhatsApp, callback)
                        else payAddon(AddonTypes.WhatsApp, undefined, callback)
                    }}>
                    <div>

                        <FlexRow className="mb-10">
                            <img src="/assets/addons/whatsapp.png" className="w-65px me-4" />
                            <div>
                                <div className="fw-bold fs-4 mb-1">
                                    WhatsApp Integration
                                </div>
                                <div className="fs-7">
                                    {moneyFormatter.format(prices?.whatsAppIntegration ?? 0)}/Month{whatsAppTrial ? " (30 Days Trial)" : ""}
                                </div>
                            </div>
                        </FlexRow>

                        {
                            whatsAppTrial ?
                                <p className="fs-6 mb-10">
                                    This add-on comes with a 30-day free trial. Your subscription will start immediately, and billing will begin after the trial period using your selected payment method.
                                </p>
                                :
                                <p className="fs-6 mb-8">
                                    Your free trial has already been used. Activating now starts the subscription immediately and charges your selected payment method.
                                </p>
                        }

                        {
                            !whatsAppTrial &&
                            <>
                                <div className="fw-bold mb-3">Payment Method</div>
                                <PaymentMethodPicker selectedMethodId={selectedMethodId} onChange={selectedMethodIdSet} />
                            </>
                        }

                    </div>
                </DrawerForm>
            }
            {
                activateTelegram &&
                <DrawerForm id="modal-activate-telegram-trial"
                    title="Activate Addon"
                    buttonText={telegramTrial ? "Start Free Trial" : `Activate & Pay ${moneyFormatter.format(prices?.telegramIntegration ?? 0)}`}
                    onClose={() => activateTelegramSet(false)}
                    onSubmit={(body, callback) => {
                        if (telegramTrial) startTrial(AddonTypes.Telegram, callback)
                        else payAddon(AddonTypes.Telegram, undefined, callback)
                    }}>
                    <div>

                        <FlexRow className="mb-10">
                            <img src="/assets/addons/telegram.png" className="w-65px me-4" />
                            <div>
                                <div className="fw-bold fs-4 mb-1">
                                    Telegram Integration
                                </div>
                                <div className="fs-7">
                                    {moneyFormatter.format(prices?.telegramIntegration ?? 0)}/Month{telegramTrial ? " (30 Days Trial)" : ""}
                                </div>
                            </div>
                        </FlexRow>

                        {
                            telegramTrial ?
                                <p className="fs-6 mb-10">
                                    This add-on comes with a 30-day free trial. Your subscription will start immediately, and billing will begin after the trial period using your selected payment method.
                                </p>
                                :
                                <p className="fs-6 mb-8">
                                    Your free trial has already been used. Activating now starts the subscription immediately and charges your selected payment method.
                                </p>
                        }

                        {
                            !telegramTrial &&
                            <>
                                <div className="fw-bold mb-3">Payment Method</div>
                                <PaymentMethodPicker selectedMethodId={selectedMethodId} onChange={selectedMethodIdSet} />
                            </>
                        }

                    </div>
                </DrawerForm>
            }



        </>
    )
}